/**
 * tRPC Provider for Console App
 *
 * Wraps the app with TRPCProvider and QueryClientProvider.
 * Configures React Query with sensible defaults for the console.
 */

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { type ReactNode, useState } from "react";
import { trpc, createTRPCClientConfig } from "@/lib/trpc";

/**
 * React Query configuration
 *
 * Optimized for the console dashboard:
 * - 30s stale time for lists (dashboard metrics, apps list)
 * - Retry failed requests once
 * - Cache unused data for 5 minutes
 */
function makeQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30 * 1000,
        refetchOnWindowFocus: import.meta.env.VITE_APP_ENV === "production",
        retry: 1,
        refetchOnMount: "always",
        gcTime: 5 * 60 * 1000,
      },
      mutations: {
        retry: false,
      },
    },
  });
}

let browserQueryClient: QueryClient | undefined = undefined;

function getQueryClient() {
  if (!browserQueryClient) {
    browserQueryClient = makeQueryClient();
  }
  return browserQueryClient;
}

/**
 * TRPCProvider Component
 *
 * Wraps the app with tRPC and React Query providers.
 */
export function TRPCProvider({ children }: { children: ReactNode }) {
  const queryClient = getQueryClient();

  const [trpcClient] = useState(() => {
    const config = createTRPCClientConfig();
    return trpc.createClient({
      links: config.links,
    });
  });

  return (
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    </trpc.Provider>
  );
}
